<template>
  <div>
    <!-- Notification Popup -->
    <div v-if="showNotification" class="late-notification">
      <div class="notification-icon">
        <img src="/resources/images/new-ui/aya-icon.svg" alt="Aya Icon" />
      </div>
      <div class="notification-content">
        <p>{{ notificationMessage }}</p>
        <div class="notification-buttons">
          <button @click="dismissNotification" class="ignore-button">Ignore</button>
          <button @click="goToAnalytics" class="inspect-button">Inspect</button>
        </div>
      </div>
      <div class="notification-time">{{ notificationTime }}</div>
    </div>

    <!-- Attendance Log Modal -->
    <a-modal
      v-model="showModal"
      title="Attendance Log"
      :width="900"
      :footer="null"
      :bodyStyle="{ padding: '24px' }"
      class="attendance-log-modal"
    >
      <div class="modal-header-controls">
        <a-date-picker
          v-model="selectedDate"
          :format="'MM/DD/YYYY'"
          @change="onDateChange"
          class="date-picker"
        />
      </div>

      <a-spin :spinning="loadingLog">
        <div class="attendance-table-wrapper">
          <table class="attendance-table">
            <thead>
              <tr>
                <th>Badge Holder</th>
                <th>Badge ID</th>
                <th>Date</th>
                <th>Expected Time</th>
                <th>Actual Time</th>
                <th>Gateway</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(record, index) in attendanceLog" :key="index">
                <td>{{ record.badgeHolder }}</td>
                <td>{{ record.badgeId }}</td>
                <td>{{ record.date }}</td>
                <td>{{ record.expectedTime }}</td>
                <td>{{ record.actualTime || '--' }}</td>
                <td>{{ record.gateway || '--' }}</td>
                <td>
                  <span :class="['status-badge', getStatusClass(record.status)]">
                    {{ record.status }}
                  </span>
                </td>
              </tr>
              <tr v-if="attendanceLog.length === 0">
                <td colspan="7" class="no-data">No attendance records found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { getLateCount, getAttendanceLog } from '@/services/api/attendance/attendance'

export default {
  name: 'LateAttendanceNotification',
  data() {
    return {
      showNotification: false,
      showModal: false,
      notificationMessage: '',
      notificationTime: '',
      lateCount: 0,
      checkInterval: null,
      attendanceLog: [],
      loadingLog: false,
      selectedDate: moment(),
      lastNotifiedCount: 0,
    }
  },
  computed: {
    authorized() {
      return this.$store.state.user.authorized
    },
  },
  watch: {
    authorized(isAuthorized) {
      if (isAuthorized) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
  },
  created() {
    if (this.authorized) {
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    startPolling() {
      if (this.checkInterval) return
      this.checkLateCount()
      this.checkInterval = setInterval(this.checkLateCount, 900 * 1000)
    },
    stopPolling() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval)
        this.checkInterval = null
      }
    },
    async checkLateCount() {
      if (!this.authorized) return

      try {
        const response = await getLateCount()
        const data = response.data

        const count = Number(data && data.count)
        if (Number.isFinite(count)) {
          // Zero late employees is not news — never pop the alert for it, and
          // clear a stale one once the count comes back down to zero.
          if (count > 0 && count !== this.lastNotifiedCount) {
            this.lateCount = count
            this.notificationMessage = data.message
            this.showNotification = true
          } else if (count === 0) {
            this.showNotification = false
          }
          this.lastNotifiedCount = count
        }
      } catch (error) {
        console.error('Failed to check late count:', error)
      }
    },
    goToAnalytics() {
      this.showNotification = false
      this.$router.push({ path: '/analytics', query: { tab: 'reports' } })
    },
    async fetchAttendanceLog() {
      this.loadingLog = true
      try {
        const dateStr = this.selectedDate.format('YYYY-MM-DD')
        const response = await getAttendanceLog(dateStr)
        const data = response.data || []
        // Sort by actualTime descending (latest check first)
        this.attendanceLog = data.sort((a, b) => {
          if (!a.actualTime) return 1
          if (!b.actualTime) return -1
          const timeA = moment(a.actualTime, ['h:mm a', 'H:mm'])
          const timeB = moment(b.actualTime, ['h:mm a', 'H:mm'])
          return timeB.valueOf() - timeA.valueOf()
        })
      } catch (error) {
        console.error('Failed to fetch attendance log:', error)
        this.attendanceLog = []
      } finally {
        this.loadingLog = false
      }
    },
    onDateChange() {
      this.fetchAttendanceLog()
    },
    dismissNotification() {
      this.showNotification = false
    },
    getStatusClass(status) {
      if (!status) return ''
      const statusLower = status.toLowerCase().replace(/\s+/g, '-')
      const statusMap = {
        'very-early': 'status-very-early',
        'early': 'status-early',
        'on-time': 'status-on-time',
        'late': 'status-late',
        'very-late': 'status-very-late',
        'absent': 'status-absent',
      }
      return statusMap[statusLower] || ''
    },
  },
}
</script>

<style scoped>
.late-notification {
  position: fixed;
  top: 30px;
  right: 30px;
  background-color: white;
  color: #152f4a;
  padding: 10px;
  border-radius: 12px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  z-index: 9999;
  max-width: 365px;
  font-family: 'Poppins', sans-serif;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.notification-icon {
  flex-shrink: 0;
  color: #9764f7;
  margin-top: 2px;
}

.notification-content {
  flex-grow: 1;
}

.notification-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.notification-content p {
  margin-bottom: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #152f4a;
  font-weight: 400;
  font-family: 'Poppins', sans-serif;
  flex: 1;
}

.notification-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.ignore-button {
  background-color: #e5e5e5;
  color: #152f4a;
  border: none;
  border-radius: 6px;
  padding: 4px 13px;
  cursor: pointer;
  font-weight: 500;
  font-size: 9px;
  transition: background-color 0.2s ease;
  font-family: 'Poppins', sans-serif;
}

.ignore-button:hover {
  background-color: #d5d5d5;
}

.inspect-button {
  background-color: #c9a9f5;
  color: #152f4a;
  border: none;
  border-radius: 6px;
  padding: 4px 13px;
  cursor: pointer;
  font-weight: 500;
  font-size: 9px;
  transition: background-color 0.2s ease;
  font-family: 'Poppins', sans-serif;
}

.inspect-button:hover {
  background-color: #b898e8;
}

.notification-time {
  flex-shrink: 0;
  font-size: 12px;
  color: #678398;
  font-family: 'Poppins', sans-serif;
  margin-left: auto;
}

/* Modal Styles */
.modal-header-controls {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.date-picker {
  width: 150px;
}

.attendance-table-wrapper {
  max-height: 500px;
  overflow-y: auto;
}

.attendance-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Poppins', sans-serif;
}

.attendance-table thead {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.attendance-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 18px;
  font-weight: 500;
  color: #152F4A;
  border-bottom: 1px solid #e2e8f0;
}

.attendance-table td {
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 400;
  color: #152F4A;
  border-bottom: 1px solid #f1f5f9;
}

.attendance-table tbody tr:hover {
  background-color: #f8fafc;
}

.no-data {
  text-align: center;
  color: #678398;
  padding: 32px 16px !important;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 101px;
  height: 32px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.status-very-early {
  background-color: #1AD697;
  color: white;
}

.status-early {
  background-color: #00803780;
  color: white;
}

.status-on-time {
  background-color: #008037F2;
  color: white;
}

.status-late {
  background-color: #FEC013;
  color: white;
}

.status-very-late {
  background-color: #E98A8B;
  color: white;
}

.status-absent {
  background-color: #979797;
  color: white;
}
</style>

<style>
/* Global modal styles */
.attendance-log-modal .ant-modal-title {
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #152F4A;
}
</style>

